import { useTrpc, type RouterInput } from '#composables/useTrpc';
import {
  PERIOD_TO_LOOKER_PERIOD,
  type ComparisonPeriodType,
  type DataTableViewType,
  type DatePeriodIntervalType,
  type ExportOptionsType,
  type KpiPeriodFilterType,
  type NielsenIntervalType,
  type NielsenPeriodIntervalType,
  type Period,
  type ProductTaxonomyType,
  type StorePerimeterType,
  DetentionPeriodValueType
} from '@carrefour-gcs/shared';
import { assertPeriodMatchesReferential } from './prepare-query-input';

interface DataTableKpiPaginationArgs {
  limit: number;
}

export type DataTableInputType = RouterInput['performance']['kpi']['getDataTableKpi'];
type DataTableQueryType = DataTableInputType['query'];
type DimensionsType = NonNullable<DataTableInputType['dimensions']>;
type SelectedKpisType = NonNullable<DataTableInputType['selectedKpis']>;
export type SortType = DataTableInputType['sort'];
interface GetDataTableKpiArgs {
  period: DatePeriodIntervalType | NielsenPeriodIntervalType;
  comparisonPeriod?: ComparisonPeriodType;
  detentionPeriod?: DetentionPeriodValueType;
  query: DataTableQueryType;
  pagination: DataTableKpiPaginationArgs;
  sort: SortType;
  dimensions: DimensionsType;
  selectedKpis: SelectedKpisType;
  taxonomy: ProductTaxonomyType;
  storePerimeter: StorePerimeterType;
  dataTableView: DataTableViewType;
  sourceContext?: DataTableInputType['sourceContext'];
  benchmark?: DataTableInputType['benchmark'];
  language: DataTableInputType['language'];
}
type ValidLookerPeriod =
  | { field: 'period_code'; value: NielsenIntervalType }
  | {
      field: 'date_key_date' | 'date_key_week' | 'date_key_month' | 'date_filter' | 'date_key';
      value: {
        start: { year: number; month: number; day: number };
        end: { year: number; month: number; day: number };
      };
    };

/** Validates the period/interval pairing and shapes it for the tRPC datatable procedures. */
function normalizeLookerPeriod(period: DatePeriodIntervalType | NielsenPeriodIntervalType): ValidLookerPeriod {
  const periodField = PERIOD_TO_LOOKER_PERIOD[period.period];
  assertPeriodMatchesReferential(periodField, period.periodInterval);

  return { field: periodField, value: period.periodInterval } as ValidLookerPeriod;
}

export async function getDataTableKpi(
  {
    period,
    comparisonPeriod,
    detentionPeriod,
    query,
    pagination: { limit },
    sort,
    dimensions,
    selectedKpis,
    taxonomy,
    storePerimeter,
    dataTableView,
    sourceContext,
    benchmark,
    language
  }: GetDataTableKpiArgs,
  signal: AbortSignal
) {
  const commonQueryInput = {
    comparisonPeriod,
    detentionPeriod,
    query,
    limit: limit ?? 6,
    sort,
    dimensions,
    selectedKpis: selectedKpis.length ? selectedKpis : undefined,
    taxonomy,
    storePerimeter,
    dataTableView,
    sourceContext,
    benchmark,
    language
  };

  return useTrpc().performance.kpi.getDataTableKpi.mutate(
    {
      period: normalizeLookerPeriod(period),
      ...commonQueryInput
    },
    { signal }
  );
}

type DataTableTotalInputType = RouterInput['performance']['kpi']['getDataTableTotalKpi'];

interface GetDataTableTotalKpiArgs {
  period: DatePeriodIntervalType | NielsenPeriodIntervalType;
  comparisonPeriod?: ComparisonPeriodType;
  detentionPeriod?: DetentionPeriodValueType;
  query: DataTableQueryType;
  selectedKpis: SelectedKpisType;
  taxonomy: ProductTaxonomyType;
  storePerimeter: StorePerimeterType;
  dataTableView: DataTableViewType;
  sourceContext?: DataTableTotalInputType['sourceContext'];
}

export async function getDataTableTotalKpi(
  {
    period,
    comparisonPeriod,
    detentionPeriod,
    query,
    selectedKpis,
    taxonomy,
    storePerimeter,
    dataTableView,
    sourceContext
  }: GetDataTableTotalKpiArgs,
  signal: AbortSignal
) {
  const commonQueryInput = {
    comparisonPeriod,
    detentionPeriod,
    query,
    selectedKpis: selectedKpis.length ? selectedKpis : undefined,
    taxonomy,
    storePerimeter,
    dataTableView,
    sourceContext
  };

  return useTrpc().performance.kpi.getDataTableTotalKpi.mutate(
    {
      period: normalizeLookerPeriod(period),
      ...commonQueryInput
    },
    { signal }
  );
}

type DataTableColumnsInputType = RouterInput['performance']['kpi']['getDataTableColumns'];

interface GetDataTableColumnsArgs {
  dataTableView: DataTableColumnsInputType['dataTableView'];
  dimensions: DataTableColumnsInputType['dimensions'];
  kpis: DataTableColumnsInputType['kpis'];
  taxonomy: DataTableColumnsInputType['taxonomy'];
  period: Period;
}
export async function getDataTableColumns(args: GetDataTableColumnsArgs) {
  const periodField = PERIOD_TO_LOOKER_PERIOD[args.period];

  return useTrpc().performance.kpi.getDataTableColumns.query({
    ...args,
    period: periodField
  });
}

type DataTableAvailableDimensionsListInputType =
  RouterInput['performance']['kpi']['getDataTableAvailableDimensionsList'];

interface GetDataTableAvailableDimensionsListArgs {
  taxonomy: DataTableAvailableDimensionsListInputType['taxonomy'];
  dataTableView: DataTableAvailableDimensionsListInputType['dataTableView'];
  period: Period;
}
export async function getDataTableAvailableDimensionsList(args: GetDataTableAvailableDimensionsListArgs) {
  const periodField = PERIOD_TO_LOOKER_PERIOD[args.period];
  return useTrpc().performance.kpi.getDataTableAvailableDimensionsList.query({
    ...args,
    period: periodField
  });
}

type DataTableAvailableKpisListInputType = RouterInput['performance']['kpi']['getDataTableAvailableKpisList'];

interface GetDataTableAvailableKpisListArgs {
  dataTableView: DataTableAvailableKpisListInputType['dataTableView'];
  taxonomy: DataTableAvailableKpisListInputType['taxonomy'];
  period: Period;
}
export async function getDataTableAvailableKpisList(args: GetDataTableAvailableKpisListArgs) {
  const periodField = PERIOD_TO_LOOKER_PERIOD[args.period];

  return useTrpc().performance.kpi.getDataTableAvailableKpisList.query({
    ...args,
    period: periodField
  });
}

type ExportInputType = RouterInput['performance']['kpi']['exportDataTable'];
type ExportQueryType = ExportInputType['query'];

interface ExportDataTableArgs {
  period: DatePeriodIntervalType | NielsenPeriodIntervalType;
  comparisonPeriod?: ComparisonPeriodType;
  detentionPeriod?: DetentionPeriodValueType;
  dataTableView: ExportInputType['dataTableView'];
  selectedDimensions: ExportInputType['selectedDimensions'];
  selectedKpis: ExportInputType['selectedKpis'];
  query: ExportQueryType;
  taxonomy: ProductTaxonomyType;
  storePerimeter: StorePerimeterType;
  benchmark?: ExportInputType['benchmark'];
  exportOptions: ExportOptionsType;
}
export async function exportDataTable({
  period,
  comparisonPeriod,
  detentionPeriod,
  dataTableView,
  selectedDimensions,
  selectedKpis,
  query,
  taxonomy,
  storePerimeter,
  benchmark,
  exportOptions
}: ExportDataTableArgs) {
  const commonQueryInput = {
    comparisonPeriod,
    detentionPeriod,
    dataTableView,
    selectedDimensions,
    selectedKpis,
    query,
    taxonomy,
    storePerimeter,
    benchmark,
    exportOptions
  };
  const periodField = PERIOD_TO_LOOKER_PERIOD[period.period];
  assertPeriodMatchesReferential(periodField, period.periodInterval);

  return useTrpc().performance.kpi.exportDataTable.mutate({
    // assertPeriodMatchesReferential just proved field and value agree; the discriminated union
    // needs a matching control-flow narrowing that a single shared assertion cannot provide.
    period: { field: periodField, value: period.periodInterval } as KpiPeriodFilterType,
    ...commonQueryInput
  });
}
